import { useRef, useEffect } from 'react';
import { MessageCircle, ChevronLeft, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Avatar } from './Avatar';
import { MessageItem } from './MessageItem';
import { MessageInput } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';

export function ChatWindow({
  conversation,
  messages,
  newMessage,
  sendingMessage,
  typingUsers,
  selectedMessageId,
  currentUserId,
  isDark,
  mobileView,
  onBack,
  onMessageChange,
  onSendMessage,
  onMessageClick,
  onDeleteClick,
  formatTime
}) {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const getConversationName = () => {
    if (!conversation) return '';
    if (conversation.type === 'group') return conversation.name;
    const other = conversation.participant_names?.find(p => p.id !== currentUserId);
    return other ? `${other.prenom} ${other.nom}` : 'Conversation';
  };

  const getConversationAvatar = () => {
    if (!conversation) return null;
    if (conversation.type === 'group') {
      return (
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
          <Users size={20} />
        </div>
      );
    }
    const other = conversation.participant_names?.find(p => p.id !== currentUserId);
    return <Avatar user={other} size="md" />;
  };

  if (!conversation) {
    return (
      <div className={cn(
        'flex-1 flex flex-col',
        mobileView === 'list' ? 'hidden md:flex' : 'flex'
      )}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <MessageCircle size={64} className={cn('mx-auto mb-4', isDark ? 'text-zinc-700' : 'text-gray-300')} />
            <p className={isDark ? 'text-zinc-500' : 'text-gray-500'}>
              Sélectionnez une conversation
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex-1 flex flex-col min-w-0',
      mobileView === 'list' ? 'hidden md:flex' : 'flex'
    )}>
      {/* Chat Header */}
      <div className={cn(
        'p-4 border-b flex items-center gap-3 flex-shrink-0',
        isDark ? 'border-white/10 bg-zinc-900/50' : 'border-gray-200 bg-gray-50'
      )}>
        <button
          onClick={onBack}
          className={cn(
            'p-2 rounded-lg md:hidden flex-shrink-0',
            isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'
          )}
        >
          <ChevronLeft size={20} />
        </button>
        {getConversationAvatar()}
        <div className="flex-1 min-w-0">
          <h3 className={cn(
            'font-semibold truncate',
            isDark ? 'text-white' : 'text-gray-900'
          )}>
            {getConversationName()}
          </h3>
          {conversation.type === 'group' && (
            <p className={cn(
              'text-xs truncate',
              isDark ? 'text-zinc-500' : 'text-gray-500'
            )}>
              {conversation.participant_names?.length} participants
            </p>
          )}
        </div>
      </div>
      
      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className={cn(
          'flex-1 overflow-y-auto p-4 space-y-3',
          isDark ? 'bg-zinc-950/50' : 'bg-gray-50'
        )}
      >
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle size={40} className={cn('mx-auto mb-2', isDark ? 'text-zinc-600' : 'text-gray-400')} />
            <p className={isDark ? 'text-zinc-500' : 'text-gray-500'}>
              Aucun message. Commencez la conversation !
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === currentUserId;
            const isDeleted = msg.deleted && msg.deleted_by !== currentUserId;
            const isDeletedByMe = msg.deleted && msg.deleted_by === currentUserId;
            const isCall = msg.message_type === 'call';
            const isSelected = selectedMessageId === msg.id;
            
            return (
              <MessageItem
                key={msg.id}
                message={msg}
                isOwn={isOwn}
                isDeleted={isDeleted}
                isDeletedByMe={isDeletedByMe}
                isCall={isCall}
                isSelected={isSelected}
                isDark={isDark}
                isGroup={conversation.type === 'group'}
                onMessageClick={() => onMessageClick(msg.id, isOwn, isDeleted)}
                onDeleteClick={(e) => onDeleteClick(e, msg.id)}
                formatTime={formatTime}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Typing indicator */}
      <TypingIndicator typingUsers={typingUsers} isDark={isDark} />
      
      {/* Message Input */}
      <MessageInput
        value={newMessage}
        onChange={onMessageChange}
        onSubmit={onSendMessage}
        disabled={sendingMessage}
        isDark={isDark}
      />
    </div>
  );
}
